// src/components/register/utils/compressImage.js

/**
 * Comprime y redimensiona una imagen en el navegador manteniendo su formato original.
 *
 * @param {File} file - Archivo de imagen original
 * @param {Object} options - Configuración de compresión
 * @param {number} options.maxDimension - Ancho o alto máximo en píxeles (default: 1600)
 * @param {number} options.quality - Calidad de compresión para JPEG/WEBP (0 a 1, default: 0.8)
 * @param {number} options.minSizeToCompress - Tamaño mínimo en bytes para aplicar compresión (default: 500KB)
 * @returns {Promise<File>} Archivo comprimido con el mismo nombre y tipo MIME
 */
export const compressImage = (file, options = {}) => {
    const {
        maxDimension = 1600,
        quality = 0.8,
        minSizeToCompress = 500 * 1024 // 500 KB
    } = options;

    return new Promise((resolve) => {
        // Si no es un archivo o es muy liviano, se devuelve sin modificar
        if (!file || !(file instanceof File) || file.size <= minSizeToCompress) {
            return resolve(file);
        }

        const mimeType = file.type || 'image/jpeg';

        // Si el formato no es compatible con canvas, devolver original
        if (!mimeType.startsWith('image/')) {
            return resolve(file);
        }

        const reader = new FileReader();

        reader.onload = (event) => {
            const img = new Image();

            img.onload = () => {
                let { width, height } = img;

                // Calcular nuevas dimensiones manteniendo la relación de aspecto
                if (width > maxDimension || height > maxDimension) {
                    if (width > height) {
                        height = Math.round((height * maxDimension) / width);
                        width = maxDimension;
                    } else {
                        width = Math.round((width * maxDimension) / height);
                        height = maxDimension;
                    }
                }

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    // Fallback si no hay soporte de contexto 2D
                    return resolve(file);
                }

                // Dibujar imagen redimensionada en el canvas
                ctx.drawImage(img, 0, 0, width, height);

                // Exportar el canvas manteniendo el tipo MIME original
                // Nota: toBlob para 'image/png' no usa parámetro de calidad en navegadores,
                // pero la reducción de dimensiones reduce el peso drásticamente.
                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            return resolve(file);
                        }

                        // Si por alguna razón el blob resultante es más pesado que el original, usar el original
                        if (blob.size >= file.size) {
                            return resolve(file);
                        }

                        // Crear un nuevo File con el mismo nombre y tipo original
                        const compressedFile = new File([blob], file.name, {
                            type: mimeType,
                            lastModified: Date.now()
                        });

                        resolve(compressedFile);
                    },
                    mimeType,
                    quality
                );
            };

            img.onerror = () => {

                resolve(file);
            };

            img.src = event.target.result;
        };

        reader.onerror = () => {

            resolve(file);
        };

        reader.readAsDataURL(file);
    });
};

/**
 * Comprime múltiples archivos de un objeto (ej. formData) de forma paralela.
 * @param {Object} fileMap - Objeto clave-valor con campos y archivos
 * @returns {Promise<Object>} Mismo objeto pero con los archivos comprimidos
 */
export const compressAllImages = async (fileMap) => {
    const keys = Object.keys(fileMap);
    const compressedEntries = await Promise.all(
        keys.map(async (key) => {
            const val = fileMap[key];
            if (val instanceof File) {
                const compressed = await compressImage(val);
                return [key, compressed];
            }
            return [key, val];
        })
    );

    return Object.fromEntries(compressedEntries);
};
